import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import * as htmlToImage from "html-to-image";
import {
  Archive, ArrowClockwise, MagnifyingGlass, Envelope, SlackLogo,
  Buildings, ArrowSquareOut, ArrowsLeftRight, TrendUp, TrendDown, Minus,
  PlusCircle, XCircle, DownloadSimple, PaperPlaneTilt, UsersThree, X,
  Trash, Plus, Camera,
} from "@phosphor-icons/react";
import client from "../lib/api";

/**
 * AdminDigestSnapshotPage — searchable archive of the last 12 weeks
 * of Sunday leaderboard digest emails. Renders the EXACT HTML that
 * shipped so admins can audit changes, forward links, and validate
 * that a specific fleet did land in the right week.
 *
 * Two modes:
 *   - `preview` (default): pick one snapshot, render its HTML in an iframe.
 *   - `compare`: pick two snapshots, get a fleet-by-fleet rank diff table.
 */
export default function AdminDigestSnapshotPage() {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [mode, setMode] = useState("preview");
  const [compareA, setCompareA] = useState("");
  const [compareB, setCompareB] = useState("");
  const [diffLoading, setDiffLoading] = useState(false);
  const [diff, setDiff] = useState(null);
  const [defaultRecipients, setDefaultRecipients] = useState([]);
  const [recipientsSource, setRecipientsSource] = useState("empty");
  const [recipientsRows, setRecipientsRows] = useState([]);
  const [recipientsModalOpen, setRecipientsModalOpen] = useState(false);
  const [newRecipientEmail, setNewRecipientEmail] = useState("");
  const [recipientBusy, setRecipientBusy] = useState(false);
  const [bulkImportText, setBulkImportText] = useState("");
  const [bulkImporting, setBulkImporting] = useState(false);
  const diffTableRef = useRef(null);
  const [pngCopying, setPngCopying] = useState(false);
  // Toggle for the "FreightPilot" watermark overlaid on PNG captures.
  // Default ON — the watermark is a mild branding win and ops usually
  // paste these into slide decks or shared docs where attribution helps.
  // Persist across page navigations via localStorage so ops don't
  // re-toggle every session.
  const [pngWatermark, setPngWatermark] = useState(() => {
    try {
      const raw = localStorage.getItem("adminDigestSnapshotPngWatermark");
      return raw === null ? true : raw === "true";
    } catch {
      return true;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("adminDigestSnapshotPngWatermark", String(pngWatermark));
    } catch {
      // localStorage disabled — keep in-memory state.
    }
  }, [pngWatermark]);
  // Position picker — where the watermark sits on the captured PNG.
  // Kept as a separate setting so admins can toggle the watermark
  // on/off without losing their preferred corner. Persisted alongside
  // the on/off flag.
  const WATERMARK_POSITIONS = [
    { key: "top-left", label: "TL", full: "Top-left" },
    { key: "top-right", label: "TR", full: "Top-right" },
    { key: "center", label: "C", full: "Center" },
    { key: "bottom-left", label: "BL", full: "Bottom-left" },
    { key: "bottom-right", label: "BR", full: "Bottom-right" },
  ];
  const [pngWatermarkPosition, setPngWatermarkPosition] = useState(() => {
    try {
      const raw = localStorage.getItem("adminDigestSnapshotPngWatermarkPos");
      const valid = WATERMARK_POSITIONS.map((p) => p.key);
      return valid.includes(raw) ? raw : "bottom-right";
    } catch {
      return "bottom-right";
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(
        "adminDigestSnapshotPngWatermarkPos", pngWatermarkPosition,
      );
    } catch {
      // localStorage disabled — degrade silently.
    }
  }, [pngWatermarkPosition]);
  // Watermark opacity — 20-80% range so it's always visible enough to
  // read but never so opaque it fights the diff-table for attention.
  // Persist alongside the on/off flag + position.
  const [pngWatermarkOpacity, setPngWatermarkOpacity] = useState(() => {
    try {
      const raw = localStorage.getItem("adminDigestSnapshotPngWatermarkOpacity");
      const n = parseInt(raw, 10);
      if (!Number.isFinite(n)) return 50;
      // Clamp to the valid range so a stale/bad value doesn't slip through.
      return Math.max(20, Math.min(80, n));
    } catch {
      return 50;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(
        "adminDigestSnapshotPngWatermarkOpacity",
        String(pngWatermarkOpacity),
      );
    } catch {
      // Silent — value persists in memory for this session.
    }
  }, [pngWatermarkOpacity]);
  // Font family for the watermark — MONO (existing default) matches
  // the digest table's data-heavy aesthetic; SANS matches most slide
  // decks. Persist alongside the other watermark settings.
  const WATERMARK_FONTS = [
    { key: "mono", label: "MONO", className: "font-mono", full: "Monospace" },
    { key: "sans", label: "SANS", className: "font-sans", full: "Sans-serif" },
  ];
  const [pngWatermarkFont, setPngWatermarkFont] = useState(() => {
    try {
      const raw = localStorage.getItem("adminDigestSnapshotPngWatermarkFont");
      return WATERMARK_FONTS.some((f) => f.key === raw) ? raw : "mono";
    } catch {
      return "mono";
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(
        "adminDigestSnapshotPngWatermarkFont", pngWatermarkFont,
      );
    } catch {
      // Silent — value persists in memory for this session.
    }
  }, [pngWatermarkFont]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get(
        "/admin/leaderboard-digest/snapshots?limit=12",
      );
      setSnapshots(data.snapshots || []);
      if ((data.snapshots || []).length && !selected) {
        setSelected(data.snapshots[0].iso_week);
      }
      // Default the compare pickers to the two most recent weeks so
      // the diff mode is one click away.
      const isos = (data.snapshots || []).map((s) => s.iso_week);
      if (isos.length >= 2) {
        if (!compareA) setCompareA(isos[1]);
        if (!compareB) setCompareB(isos[0]);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load snapshots");
    } finally {
      setLoading(false);
    }
  };

  const loadDiff = async (a, b) => {
    if (!a || !b || a === b) {
      setDiff(null);
      return;
    }
    setDiffLoading(true);
    try {
      const { data } = await client.get(
        `/admin/leaderboard-digest/snapshots/diff/${encodeURIComponent(a)}/${encodeURIComponent(b)}`,
      );
      setDiff(data);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || `Failed to diff ${a} vs ${b}`,
      );
      setDiff(null);
    } finally {
      setDiffLoading(false);
    }
  };

  const csvEscape = (val) => {
    if (val === null || val === undefined) return "";
    const s = String(val);
    // Escape if the cell contains a comma, quote, or newline
    if (s.includes(",") || s.includes('"') || s.includes("\n")) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };

  const downloadDiffCsv = () => {
    if (!diff || !diff.changes) return;
    const header = [
      "company_name", "owner_email",
      `rank_${diff.a?.iso_week || "A"}`,
      `rank_${diff.b?.iso_week || "B"}`,
      "delta_rank",
      `total_${diff.a?.iso_week || "A"}`,
      `total_${diff.b?.iso_week || "B"}`,
      "verdict",
    ];
    const summary = diff.summary || {};
    // Prepend the totals row so email recipients see the shape at a
    // glance without scrolling the whole diff. Kept as a single row
    // with the same column count so CSV importers don't choke.
    const summaryRow = [
      "TOTALS",
      `${diff.a?.iso_week || "A"} → ${diff.b?.iso_week || "B"}`,
      `climbers=${summary.climbers || 0}`,
      `held=${summary.held || 0}`,
      `dropped=${summary.droppers || 0}`,
      `new=${summary.new || 0}`,
      `left=${summary.left || 0}`,
      `total_fleets=${(diff.changes || []).length}`,
    ];
    const lines = [
      summaryRow.map(csvEscape).join(","),
      header.map(csvEscape).join(","),
    ];
    for (const c of diff.changes) {
      lines.push([
        c.company_name || "",
        c.owner_email || "",
        c.rank_a ?? "",
        c.rank_b ?? "",
        c.delta ?? "",
        c.total_a ?? 0,
        c.total_b ?? 0,
        c.verdict || "",
      ].map(csvEscape).join(","));
    }
    const csv = lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leaderboard-diff-${diff.a?.iso_week}-vs-${diff.b?.iso_week}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(
      `Downloaded ${diff.changes.length} row${diff.changes.length !== 1 ? "s" : ""} + summary as CSV`,
    );
  };

  const emailDiffToTeam = () => {
    if (!diff) return;
    const s = diff.summary || {};
    const subject = `Leaderboard movement · ${diff.a?.iso_week} → ${diff.b?.iso_week}`;
    // Top 5 climbers as a preview so recipients see something concrete
    // without opening the CSV.
    const climbers = (diff.changes || [])
      .filter((c) => c.verdict === "climbed")
      .slice(0, 5)
      .map((c, i) => `${i + 1}. ${c.company_name || "Unknown"} — climbed ${c.delta} to #${c.rank_b}`)
      .join("\n");
    const bodyLines = [
      `Weekly leaderboard movement — ${diff.a?.iso_week} vs ${diff.b?.iso_week}`,
      "",
      `Totals: ${s.climbers || 0} climbed · ${s.held || 0} held · ${s.droppers || 0} dropped · ${s.new || 0} new · ${s.left || 0} left`,
      `Total fleets tracked: ${(diff.changes || []).length}`,
      "",
    ];
    if (climbers) {
      bodyLines.push("Top climbers:", climbers, "");
    }
    bodyLines.push(
      "Full CSV attached (download separately via Digest Snapshots → Compare → Download CSV).",
      "",
      "— FreightPilot AI · admin digest",
    );
    const mailto = `mailto:${encodeURIComponent(defaultRecipients.join(","))}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyLines.join("\n"))}`;
    window.location.href = mailto;
  };

  const copyDiffAsPng = async () => {
    if (!diffTableRef.current) return;
    setPngCopying(true);
    // Reveal the watermark div for the duration of the capture only.
    // Rendered permanently in the tree so `html-to-image` picks up its
    // real position/size, but hidden with opacity:0 on-page so admins
    // aren't distracted by branding text over the diff table.
    const wm = diffTableRef.current.querySelector("[data-watermark]");
    if (wm && pngWatermark) {
      // Use the picked opacity (20-80%) so admins can dial the
      // watermark from a whisper to a bold signature. Store as a
      // decimal string for the inline style.
      wm.style.opacity = String(pngWatermarkOpacity / 100);
    }
    try {
      // Render the DOM node to a PNG blob at 2x scale for crisp
      // slide-deck quality. `backgroundColor` matches the dark theme.
      const blob = await htmlToImage.toBlob(diffTableRef.current, {
        pixelRatio: 2,
        backgroundColor: "#0b0b0d",
        style: { padding: "16px" },
      });
      if (!blob) throw new Error("empty blob");

      // Try the modern clipboard image API first — Slack, Slides,
      // Notion all accept image/png paste. Falls back to a download
      // when the browser blocks clipboard writes (Firefox, Safari
      // sans permission).
      if (typeof ClipboardItem !== "undefined"
          && navigator.clipboard?.write) {
        try {
          await navigator.clipboard.write([
            new ClipboardItem({ "image/png": blob }),
          ]);
          toast.success(
            `Diff table copied as PNG${pngWatermark ? " (watermarked)" : ""} — paste anywhere.`,
          );
          return;
        } catch {
          // Clipboard permission denied — fall through to download.
        }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `leaderboard-diff-${diff.a?.iso_week}-vs-${diff.b?.iso_week}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.info(
        "Clipboard blocked by your browser — PNG downloaded instead.",
      );
    } catch (e) {
      toast.error(`PNG capture failed: ${e?.message || "unknown"}`);
    } finally {
      if (wm) wm.style.opacity = "";
      setPngCopying(false);
    }
  };

  const loadDetail = async (iso) => {
    if (!iso) return;
    setDetailLoading(true);
    try {
      const { data } = await client.get(
        `/admin/leaderboard-digest/snapshots/${encodeURIComponent(iso)}`,
      );
      setDetail(data);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || `Failed to load ${iso}`,
      );
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    // Load env-driven default `mailto:` recipient list once so the
    // Email-to-Team button pre-fills without ops retyping addresses.
    loadRecipients();
  }, []);

  const loadRecipients = async () => {
    try {
      const { data } = await client.get("/admin/settings/diff-recipients");
      setDefaultRecipients(data.recipients || []);
      setRecipientsSource(data.source || "empty");
      setRecipientsRows(data.rows || []);
    } catch {}
  };

  const addRecipient = async () => {
    const email = newRecipientEmail.trim();
    if (!email) return;
    setRecipientBusy(true);
    try {
      const { data } = await client.post(
        "/admin/settings/diff-recipients", { email },
      );
      if (data.already_present) {
        toast.info(`${email} was already on the list.`);
      } else {
        toast.success(`${email} added to recipients.`);
      }
      setNewRecipientEmail("");
      await loadRecipients();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Failed to add recipient",
      );
    } finally {
      setRecipientBusy(false);
    }
  };

  const removeRecipient = async (email) => {
    setRecipientBusy(true);
    try {
      await client.delete(
        `/admin/settings/diff-recipients/${encodeURIComponent(email)}`,
      );
      toast.success(`${email} removed.`);
      await loadRecipients();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || `Failed to remove ${email}`,
      );
    } finally {
      setRecipientBusy(false);
    }
  };

  const bulkImportRecipients = async () => {
    // Split on newlines, commas, semicolons, and whitespace so ops
    // can paste from any source (spreadsheet column, prev email
    // header, Slack list) and it just works.
    const raw = bulkImportText || "";
    const emails = raw.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
    if (!emails.length) {
      toast.info("Paste some emails first.");
      return;
    }
    setBulkImporting(true);
    try {
      const { data } = await client.post(
        "/admin/settings/diff-recipients/bulk-import",
        { emails },
      );
      const parts = [];
      if (data.added) parts.push(`${data.added} added`);
      if (data.already_present) parts.push(`${data.already_present} skipped`);
      if (data.invalid) parts.push(`${data.invalid} invalid`);
      toast.success(parts.join(" · ") || "Import complete");
      setBulkImportText("");
      await loadRecipients();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Bulk import failed",
      );
    } finally {
      setBulkImporting(false);
    }
  };

  // Live parse of the bulk-import textarea so ops sees "N valid ·
  // N invalid · N duplicate" before clicking Import. Same regex the
  // submit uses so counts match exactly.
  const bulkImportPreview = useMemo(() => {
    const raw = bulkImportText || "";
    const emails = raw.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
    const known = new Set(defaultRecipients.map((e) => e.toLowerCase()));
    const seen = new Set();
    let valid = 0;
    let invalid = 0;
    let duplicate = 0;
    let alreadyOnList = 0;
    for (const e of emails) {
      const el = e.toLowerCase();
      if (seen.has(el)) {
        duplicate += 1;
        continue;
      }
      seen.add(el);
      const [local, _at, domain] = [
        el.split("@")[0] || "", "@", el.split("@")[1] || "",
      ];
      if (!local || !domain || !domain.includes(".")) {
        invalid += 1;
        continue;
      }
      if (known.has(el)) {
        alreadyOnList += 1;
        continue;
      }
      valid += 1;
    }
    return {
      total: emails.length,
      valid,
      invalid,
      duplicate,
      alreadyOnList,
    };
  }, [bulkImportText, defaultRecipients]);

  // Tokenize the bulk-import textarea into alternating token / separator
  // segments so an overlay `<pre>` can underline just the invalid
  // tokens in place — matching the exact character positions in the
  // textarea. Whitespace is preserved verbatim so the overlay aligns
  // 1:1 with the textarea (both use `whitespace-pre-wrap` + monospace).
  const bulkImportHighlight = useMemo(() => {
    const text = bulkImportText || "";
    if (!text) return [];
    const parts = text.split(/([\s,;]+)/);
    return parts.map((seg, idx) => {
      if (!seg) return { text: seg, invalid: false, key: idx };
      if (/^[\s,;]+$/.test(seg)) {
        return { text: seg, invalid: false, key: idx };
      }
      const el = seg.trim().toLowerCase();
      const local = el.split("@")[0] || "";
      const domain = el.split("@")[1] || "";
      const isInvalid = !local || !domain || !domain.includes(".");
      return { text: seg, invalid: isInvalid, key: idx };
    });
  }, [bulkImportText]);
  // Refs used for scroll-sync between the textarea and its overlay so
  // underlines stay glued to the tokens even after the user scrolls
  // past row 4 of a big paste.
  const bulkImportTextareaRef = useRef(null);
  const bulkImportOverlayRef = useRef(null);
  const _syncBulkImportScroll = () => {
    if (bulkImportOverlayRef.current && bulkImportTextareaRef.current) {
      bulkImportOverlayRef.current.scrollTop
        = bulkImportTextareaRef.current.scrollTop;
      bulkImportOverlayRef.current.scrollLeft
        = bulkImportTextareaRef.current.scrollLeft;
    }
  };
  useEffect(() => { if (selected) loadDetail(selected); }, [selected]);
  useEffect(() => {
    if (mode === "compare") loadDiff(compareA, compareB);
  }, [mode, compareA, compareB]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return snapshots;
    return snapshots.filter((s) => {
      const iso = (s.iso_week || "").toLowerCase();
      const subj = (s.subject || "").toLowerCase();
      const recips = (s.recipients || []).join(",").toLowerCase();
      return iso.includes(q) || subj.includes(q) || recips.includes(q);
    });
  }, [snapshots, query]);

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString(undefined, {
        month: "short", day: "numeric", year: "numeric",
        hour: "numeric", minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div
      className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8"
      data-testid="admin-digest-snapshot-page"
    >
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <div className="pill pill-info mb-2 inline-flex items-center gap-1.5">
            <Archive size={11} weight="fill" /> ADMIN · DIGEST ARCHIVE
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white leading-tight">
            Weekly Digest Snapshots
          </h1>
          <p className="text-neutral-400 text-sm mt-2 max-w-2xl">
            Searchable archive of the last 12 weeks of Sunday leaderboard
            digest emails. Click any row to preview the exact HTML that shipped.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="admin-digest-snapshot-refresh"
          className="btn-secondary rounded-full px-4 py-2 text-xs inline-flex items-center gap-1.5 shrink-0"
        >
          <ArrowClockwise size={12} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Mode toggle: Preview vs Compare */}
      <div className="flex gap-2 mb-4" data-testid="admin-digest-snapshot-mode-toggle">
        {[
          { key: "preview", label: "Preview", icon: <Archive size={12} /> },
          { key: "compare", label: "Compare weeks", icon: <ArrowsLeftRight size={12} /> },
        ].map((m) => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            data-testid={`admin-digest-snapshot-mode-${m.key}`}
            className={`text-xs rounded-full px-4 py-2 transition-colors border inline-flex items-center gap-1.5 ${
              mode === m.key
                ? "bg-[#FF5A1F] border-[#FF5A1F] text-white font-semibold"
                : "border-white/10 text-neutral-400 hover:text-white hover:border-white/30"
            }`}
          >
            {m.icon} {m.label}
          </button>
        ))}
      </div>

      {/* Search (preview mode only) */}
      {mode === "preview" && (
        <div className="mb-4 relative max-w-md">
          <MagnifyingGlass
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500"
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search iso week, subject, recipient…"
            data-testid="admin-digest-snapshot-search"
            className="w-full bg-white/[0.03] border border-white/10 rounded-full pl-9 pr-4 py-2 text-sm text-white placeholder:text-neutral-500 focus:outline-none focus:border-[#FF5A1F]/40"
          />
        </div>
      )}

      {loading && snapshots.length === 0 && (
        <div className="card-surface p-8 text-center text-neutral-400">
          Loading snapshots…
        </div>
      )}

      {!loading && snapshots.length === 0 && (
        <div
          className="card-surface p-8 text-center"
          data-testid="admin-digest-snapshot-empty"
        >
          <div className="mb-3 text-neutral-400">
            No digest snapshots yet.
          </div>
          <div className="text-xs text-neutral-500">
            The Sunday leaderboard digest runs weekly. Trigger one now
            from the digest admin page to seed the archive.
          </div>
        </div>
      )}

      {/* Compare mode UI */}
      {!loading && snapshots.length > 0 && mode === "compare" && (
        <div className="space-y-4" data-testid="admin-digest-snapshot-compare">
          {/* Week pickers */}
          <div className="card-surface p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { label: "Baseline (A)", value: compareA, setter: setCompareA, testId: "a" },
                { label: "Comparison (B)", value: compareB, setter: setCompareB, testId: "b" },
              ].map((picker) => (
                <div key={picker.testId}>
                  <label className="block text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5">
                    {picker.label}
                  </label>
                  <select
                    value={picker.value}
                    onChange={(e) => picker.setter(e.target.value)}
                    data-testid={`admin-digest-snapshot-compare-select-${picker.testId}`}
                    className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#FF5A1F]/40"
                  >
                    <option value="">— Pick a week —</option>
                    {snapshots.map((s) => (
                      <option key={s.iso_week} value={s.iso_week}>
                        {s.iso_week} · {s.fleets_on_board || 0} fleets · {s.total_shares_this_week || 0} shares
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            {compareA && compareB && compareA === compareB && (
              <div
                className="mt-3 text-xs text-amber-300"
                data-testid="admin-digest-snapshot-compare-same-warning"
              >
                Pick two different weeks to see a diff.
              </div>
            )}
          </div>

          {diffLoading && (
            <div
              className="card-surface p-8 text-center text-neutral-400"
              data-testid="admin-digest-snapshot-diff-loading"
            >
              Loading diff…
            </div>
          )}

          {!diffLoading && diff && (
            <>
              {/* Summary strip + CSV export */}
              <div className="flex items-start justify-between gap-3">
                <div
                  className="grid grid-cols-2 sm:grid-cols-5 gap-3 flex-1"
                  data-testid="admin-digest-snapshot-diff-summary"
                >
                {[
                  { key: "climbers", label: "Climbed", color: "emerald", icon: <TrendUp size={14} weight="fill" /> },
                  { key: "held", label: "Held", color: "neutral", icon: <Minus size={14} weight="bold" /> },
                  { key: "droppers", label: "Dropped", color: "amber", icon: <TrendDown size={14} weight="fill" /> },
                  { key: "new", label: "New", color: "sky", icon: <PlusCircle size={14} weight="fill" /> },
                  { key: "left", label: "Left", color: "rose", icon: <XCircle size={14} weight="fill" /> },
                ].map((tile) => {
                  const styles = {
                    emerald: "text-emerald-300",
                    amber: "text-amber-300",
                    sky: "text-sky-300",
                    rose: "text-rose-300",
                    neutral: "text-neutral-300",
                  };
                  return (
                    <div
                      key={tile.key}
                      className="rounded-lg bg-white/[0.03] border border-white/5 p-3"
                    >
                      <div className={`inline-flex items-center gap-1 ${styles[tile.color]} text-[10px] uppercase tracking-widest`}>
                        {tile.icon} {tile.label}
                      </div>
                      <div className={`text-2xl font-mono font-bold mt-1 ${styles[tile.color]}`}>
                        {(diff.summary && diff.summary[tile.key]) || 0}
                      </div>
                    </div>
                  );
                })}
                </div>
                <div className="flex flex-col gap-2 shrink-0 self-start">
                  <button
                    onClick={downloadDiffCsv}
                    data-testid="admin-digest-snapshot-diff-csv"
                    className="btn-secondary rounded-full px-4 py-2 text-xs inline-flex items-center gap-1.5"
                    title="Download this diff as a CSV"
                  >
                    <DownloadSimple size={12} weight="bold" />
                    Download CSV
                  </button>
                  <button
                    onClick={copyDiffAsPng}
                    disabled={pngCopying}
                    data-testid="admin-digest-snapshot-diff-png"
                    className="rounded-full px-4 py-2 text-xs border border-white/15 text-neutral-200 hover:bg-white/[0.04] hover:border-white/30 disabled:opacity-50 inline-flex items-center gap-1.5 transition-colors"
                    title="Copy the diff table as a PNG for slides"
                  >
                    <Camera size={12} weight="bold" />
                    {pngCopying ? "Capturing…" : "Copy as PNG"}
                  </button>
                  {/* Watermark toggle — pill-switch that persists to
                      localStorage. Sits right under the Copy-as-PNG
                      button so ops can flip it inline before capture. */}
                  <button
                    type="button"
                    role="switch"
                    aria-checked={pngWatermark}
                    onClick={() => setPngWatermark((v) => !v)}
                    data-testid="admin-digest-snapshot-diff-png-watermark-toggle"
                    className={`rounded-full px-3 py-1 text-[10px] uppercase tracking-widest inline-flex items-center gap-1.5 border transition-colors ${
                      pngWatermark
                        ? "bg-[#FF5A1F]/[0.12] border-[#FF5A1F]/40 text-[#FF5A1F]"
                        : "border-white/10 text-neutral-500 hover:text-neutral-300 hover:border-white/20"
                    }`}
                    title="Overlay a small FreightPilot watermark on the captured PNG"
                  >
                    <span
                      className={`w-2 h-2 rounded-full ${
                        pngWatermark ? "bg-[#FF5A1F]" : "bg-neutral-600"
                      }`}
                    />
                    Watermark {pngWatermark ? "on" : "off"}
                  </button>
                  {/* Position picker — five-segment control that fades
                      in only when watermark is on. Small letter labels
                      (TL/TR/C/BL/BR) keep the row compact; full names
                      surface via `title=` tooltip. */}
                  {pngWatermark && (
                    <div
                      className="flex items-center gap-0.5 rounded-full border border-white/10 bg-white/[0.02] p-0.5"
                      data-testid="admin-digest-snapshot-diff-png-watermark-position-picker"
                    >
                      {WATERMARK_POSITIONS.map((pos) => (
                        <button
                          key={pos.key}
                          type="button"
                          onClick={() => setPngWatermarkPosition(pos.key)}
                          data-testid={`admin-digest-snapshot-diff-png-watermark-position-${pos.key}`}
                          className={`text-[9px] font-mono w-6 h-5 rounded-full inline-flex items-center justify-center transition-colors ${
                            pngWatermarkPosition === pos.key
                              ? "bg-[#FF5A1F] text-white font-bold"
                              : "text-neutral-500 hover:text-white"
                          }`}
                          title={`Watermark position: ${pos.full}`}
                          aria-label={`Watermark position ${pos.full}`}
                        >
                          {pos.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {/* Opacity slider — 20-80% so the watermark is always
                      visible enough to read but never so heavy it
                      fights the table. Compact range + live percent
                      readout in the same pill. */}
                  {pngWatermark && (
                    <div
                      className="flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.02] px-3 py-1"
                      data-testid="admin-digest-snapshot-diff-png-watermark-opacity"
                    >
                      <span className="text-[9px] uppercase tracking-widest text-neutral-500 shrink-0">
                        Opacity
                      </span>
                      <input
                        type="range"
                        min={20}
                        max={80}
                        step={5}
                        value={pngWatermarkOpacity}
                        onChange={(e) => setPngWatermarkOpacity(parseInt(e.target.value, 10))}
                        data-testid="admin-digest-snapshot-diff-png-watermark-opacity-slider"
                        className="w-20 accent-[#FF5A1F] cursor-pointer"
                        aria-label="Watermark opacity percentage"
                      />
                      <span
                        className="text-[10px] font-mono tabular-nums text-[#FF5A1F] w-8 text-right"
                        data-testid="admin-digest-snapshot-diff-png-watermark-opacity-value"
                      >
                        {pngWatermarkOpacity}%
                      </span>
                    </div>
                  )}
                  {/* Font-family toggle — MONO matches the data-heavy
                      diff table; SANS matches slide-deck body text so
                      the watermark blends in when pasted into decks.
                      Only two options → keep it as a compact pill. */}
                  {pngWatermark && (
                    <div
                      className="flex items-center gap-0.5 rounded-full border border-white/10 bg-white/[0.02] p-0.5"
                      data-testid="admin-digest-snapshot-diff-png-watermark-font-picker"
                    >
                      {WATERMARK_FONTS.map((f) => (
                        <button
                          key={f.key}
                          type="button"
                          onClick={() => setPngWatermarkFont(f.key)}
                          data-testid={`admin-digest-snapshot-diff-png-watermark-font-${f.key}`}
                          className={`text-[9px] px-2 h-5 rounded-full inline-flex items-center justify-center transition-colors ${f.className} ${
                            pngWatermarkFont === f.key
                              ? "bg-[#FF5A1F] text-white font-bold"
                              : "text-neutral-500 hover:text-white"
                          }`}
                          title={`Watermark typeface: ${f.full}`}
                          aria-label={`Watermark typeface ${f.full}`}
                        >
                          {f.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {/* Live preview chip — dashed-rectangle scale model of
                      the diff table with the watermark placed at the
                      picked corner, in the picked font and opacity.
                      Ops sees the exact result before hitting Copy as
                      PNG so they don't waste a capture on a wrong
                      corner. */}
                  {pngWatermark && (
                    <div
                      className="flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.02] px-2 py-1"
                      data-testid="admin-digest-snapshot-diff-png-watermark-preview-chip"
                    >
                      <span className="text-[9px] uppercase tracking-widest text-neutral-500 shrink-0">
                        Preview
                      </span>
                      <div
                        className="relative w-16 h-8 border border-dashed border-white/25 rounded-sm bg-white/[0.02] overflow-hidden"
                        aria-label="Watermark preview"
                      >
                        <span
                          data-testid="admin-digest-snapshot-diff-png-watermark-preview-text"
                          className={`absolute text-[6px] leading-tight uppercase tracking-widest text-white whitespace-nowrap select-none ${
                            pngWatermarkFont === "sans" ? "font-sans" : "font-mono"
                          } ${
                            pngWatermarkPosition === "top-left"
                              ? "top-0.5 left-1"
                            : pngWatermarkPosition === "top-right"
                              ? "top-0.5 right-1"
                            : pngWatermarkPosition === "center"
                              ? "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                            : pngWatermarkPosition === "bottom-left"
                              ? "bottom-0.5 left-1"
                            : "bottom-0.5 right-1"
                          }`}
                          style={{ opacity: pngWatermarkOpacity / 100 }}
                        >
                          FreightPilot
                        </span>
                      </div>
                    </div>
                  )}
                  <button
                    onClick={emailDiffToTeam}
                    data-testid="admin-digest-snapshot-diff-email"
                    className="rounded-full px-4 py-2 text-xs border border-white/15 text-neutral-200 hover:bg-white/[0.04] hover:border-white/30 inline-flex items-center gap-1.5 transition-colors"
                    title="Compose an email with the diff summary"
                  >
                    <PaperPlaneTilt size={12} weight="bold" />
                    Email to team
                    {defaultRecipients.length > 0 && (
                      <span className="text-[9px] bg-white/10 text-neutral-400 rounded-full px-1.5 py-0.5">
                        {defaultRecipients.length}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setRecipientsModalOpen(true)}
                    data-testid="admin-digest-snapshot-recipients-manage-btn"
                    className="rounded-full px-4 py-1.5 text-[11px] text-neutral-400 hover:text-white inline-flex items-center gap-1.5 transition-colors"
                    title="Manage the email-to-team recipient list"
                  >
                    <UsersThree size={11} weight="bold" />
                    Manage recipients
                  </button>
                </div>
              </div>

              {/* Diff table */}
              <div
                ref={diffTableRef}
                className="card-surface overflow-hidden relative"
                data-testid="admin-digest-snapshot-diff-table-container"
              >
                {/* Watermark — always rendered so html-to-image sees it
                    in the DOM, but opacity:0 by default so admins don't
                    see it on-page. `copyDiffAsPng` flips opacity to 1
                    just before capture and back to "" after. Position
                    driven by the picker (top-left / top-right / center
                    / bottom-left / bottom-right). */}
                <div
                  data-watermark
                  data-testid="admin-digest-snapshot-diff-png-watermark"
                  data-position={pngWatermarkPosition}
                  data-font={pngWatermarkFont}
                  className={`pointer-events-none absolute text-[10px] uppercase tracking-widest text-white/50 select-none transition-opacity ${
                    pngWatermarkFont === "sans" ? "font-sans" : "font-mono"
                  } ${
                    pngWatermarkPosition === "top-left"
                      ? "top-2 left-3"
                    : pngWatermarkPosition === "top-right"
                      ? "top-2 right-3"
                    : pngWatermarkPosition === "center"
                      ? "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white/40 text-xs"
                    : pngWatermarkPosition === "bottom-left"
                      ? "bottom-2 left-3"
                    : "bottom-2 right-3"
                  }`}
                  style={{ opacity: 0 }}
                >
                  FreightPilot AI · admin digest · {diff.a?.iso_week} → {diff.b?.iso_week}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-white/[0.02]">
                      <tr className="text-[10px] uppercase tracking-widest text-neutral-500">
                        <th className="text-left px-4 py-3">Fleet</th>
                        <th className="text-right px-4 py-3">
                          Rank A · {diff.a?.iso_week}
                        </th>
                        <th className="text-right px-4 py-3">
                          Rank B · {diff.b?.iso_week}
                        </th>
                        <th className="text-right px-4 py-3">Δ Rank</th>
                        <th className="text-right px-4 py-3">
                          Total A → B
                        </th>
                        <th className="text-right px-4 py-3">Verdict</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(diff.changes || []).map((row) => {
                        const verdictStyles = {
                          climbed: "bg-emerald-500/10 text-emerald-300 border-emerald-500/40",
                          dropped: "bg-amber-500/10 text-amber-300 border-amber-500/40",
                          held: "bg-white/[0.04] text-neutral-400 border-white/10",
                          new: "bg-sky-500/10 text-sky-300 border-sky-500/40",
                          left: "bg-rose-500/10 text-rose-300 border-rose-500/40",
                        };
                        const verdictLabels = {
                          climbed: "Climbed", dropped: "Dropped",
                          held: "Held", new: "New", left: "Left",
                        };
                        return (
                          <tr
                            key={row.owner_id}
                            data-testid={`admin-digest-snapshot-diff-row-${row.owner_id}`}
                            className="border-t border-white/[0.04] hover:bg-white/[0.02] transition-colors"
                          >
                            <td className="px-4 py-3">
                              <div className="text-white font-medium truncate max-w-[280px]">
                                {row.company_name || "Unknown"}
                              </div>
                              <div className="text-[11px] text-neutral-500 font-mono truncate">
                                {row.owner_email}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right text-neutral-300 font-mono tabular-nums">
                              {row.rank_a ?? "—"}
                            </td>
                            <td className="px-4 py-3 text-right text-white font-mono tabular-nums font-bold">
                              {row.rank_b ?? "—"}
                            </td>
                            <td className="px-4 py-3 text-right font-mono tabular-nums">
                              {row.delta === null || row.delta === undefined ? (
                                <span className="text-neutral-600">—</span>
                              ) : row.delta > 0 ? (
                                <span className="text-emerald-300">+{row.delta}</span>
                              ) : row.delta < 0 ? (
                                <span className="text-amber-300">{row.delta}</span>
                              ) : (
                                <span className="text-neutral-500">0</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right text-neutral-400 font-mono tabular-nums text-[11px]">
                              {row.total_a || 0} → {row.total_b || 0}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span
                                className={`inline-flex items-center text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 border ${
                                  verdictStyles[row.verdict] || verdictStyles.held
                                }`}
                                data-testid={`admin-digest-snapshot-diff-verdict-${row.owner_id}`}
                              >
                                {verdictLabels[row.verdict] || row.verdict}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {!diffLoading && !diff && compareA && compareB && compareA !== compareB && (
            <div
              className="card-surface p-8 text-center text-neutral-500 text-sm"
              data-testid="admin-digest-snapshot-diff-empty"
            >
              No comparable snapshot data for these weeks.
            </div>
          )}
        </div>
      )}

      {!loading && snapshots.length > 0 && mode === "preview" && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Left: list */}
          <div
            className="lg:col-span-2 card-surface overflow-hidden max-h-[720px] overflow-y-auto"
            data-testid="admin-digest-snapshot-list"
          >
            {filtered.length === 0 && (
              <div
                className="p-6 text-center text-neutral-500 text-sm"
                data-testid="admin-digest-snapshot-empty-search"
              >
                No snapshots match "{query}".
              </div>
            )}
            {filtered.map((snap) => {
              const isSelected = snap.iso_week === selected;
              return (
                <button
                  key={snap.iso_week}
                  onClick={() => setSelected(snap.iso_week)}
                  data-testid={`admin-digest-snapshot-row-${snap.iso_week}`}
                  className={`w-full text-left border-b border-white/[0.04] px-4 py-3 hover:bg-white/[0.02] transition-colors ${
                    isSelected ? "bg-[#FF5A1F]/[0.06] border-l-2 border-l-[#FF5A1F]" : ""
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-mono font-bold text-white">
                      {snap.iso_week}
                    </div>
                    <div className="flex items-center gap-1.5">
                      {snap.slack_posted && (
                        <span
                          className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest bg-emerald-500/10 text-emerald-300 border border-emerald-500/40 rounded-full px-2 py-0.5"
                          data-testid={`admin-digest-snapshot-slack-${snap.iso_week}`}
                        >
                          <SlackLogo size={9} weight="fill" /> Slack
                        </span>
                      )}
                      <span
                        className={`inline-flex items-center text-[9px] uppercase tracking-widest rounded-full px-2 py-0.5 border ${
                          snap.status === "sent"
                            ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/40"
                            : "bg-amber-500/10 text-amber-300 border-amber-500/40"
                        }`}
                      >
                        {snap.status || "unknown"}
                      </span>
                    </div>
                  </div>
                  <div className="text-[11px] text-neutral-500 mt-1 truncate">
                    {snap.subject || "Weekly leaderboard digest"}
                  </div>
                  <div className="flex items-center gap-3 text-[10px] text-neutral-500 mt-1.5 font-mono">
                    <span className="inline-flex items-center gap-1">
                      <Envelope size={9} weight="fill" />
                      {snap.sent_count || 0} sent
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Buildings size={9} weight="fill" />
                      {snap.fleets_on_board || 0} fleets
                    </span>
                    <span>· {snap.total_shares_this_week || 0} shares</span>
                  </div>
                  <div className="text-[10px] text-neutral-600 mt-1">
                    {snap.fired_at ? fmtDate(snap.fired_at) : ""}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Right: preview */}
          <div className="lg:col-span-3">
            <div className="card-surface overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.04]">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                    Preview
                  </div>
                  <div className="text-sm font-mono font-bold text-white mt-0.5">
                    {selected || "—"}
                  </div>
                </div>
                {detail?.recipients?.length > 0 && (
                  <div
                    className="text-[11px] text-neutral-400 max-w-xs truncate text-right"
                    data-testid="admin-digest-snapshot-recipients"
                    title={detail.recipients.join(", ")}
                  >
                    → {detail.recipients.join(", ")}
                  </div>
                )}
              </div>
              {detailLoading && (
                <div
                  className="p-8 text-center text-neutral-500 text-sm"
                  data-testid="admin-digest-snapshot-preview-loading"
                >
                  Loading HTML…
                </div>
              )}
              {!detailLoading && !detail && (
                <div className="p-8 text-center text-neutral-500 text-sm">
                  Select a snapshot to preview.
                </div>
              )}
              {!detailLoading && detail?.html && (
                <div className="bg-white">
                  <iframe
                    key={detail.iso_week}
                    srcDoc={detail.html}
                    title={`Digest snapshot ${detail.iso_week}`}
                    className="w-full min-h-[700px] border-0"
                    sandbox=""
                    data-testid={`admin-digest-snapshot-iframe-${detail.iso_week}`}
                  />
                </div>
              )}
              {!detailLoading && detail && !detail.html && (
                <div
                  className="p-8 text-center text-neutral-500 text-sm"
                  data-testid="admin-digest-snapshot-no-html"
                >
                  <div className="mb-2">No HTML stored for this snapshot.</div>
                  <div className="text-[11px] text-neutral-600">
                    Snapshots recorded before the archive feature shipped
                    won't have HTML. Future runs will.
                  </div>
                </div>
              )}
            </div>

            {detail && (
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="rounded-lg bg-white/[0.03] border border-white/5 p-3">
                  <div className="text-[9px] uppercase tracking-widest text-neutral-500">Shares</div>
                  <div className="text-lg font-mono font-bold text-white mt-1">
                    {detail.total_shares_this_week || 0}
                  </div>
                </div>
                <div className="rounded-lg bg-white/[0.03] border border-white/5 p-3">
                  <div className="text-[9px] uppercase tracking-widest text-neutral-500">Fleets</div>
                  <div className="text-lg font-mono font-bold text-white mt-1">
                    {detail.fleets_on_board || 0}
                  </div>
                </div>
                <div className="rounded-lg bg-white/[0.03] border border-white/5 p-3">
                  <div className="text-[9px] uppercase tracking-widest text-neutral-500">Sent</div>
                  <div className="text-lg font-mono font-bold text-emerald-300 mt-1">
                    {detail.sent_count || 0}
                  </div>
                </div>
                <div className="rounded-lg bg-white/[0.03] border border-white/5 p-3">
                  <div className="text-[9px] uppercase tracking-widest text-neutral-500">Slack</div>
                  <div
                    className={`text-lg font-mono font-bold mt-1 inline-flex items-center gap-1 ${
                      detail.slack_posted ? "text-emerald-300" : "text-neutral-500"
                    }`}
                  >
                    <SlackLogo size={14} weight="fill" />
                    {detail.slack_posted ? "posted" : "—"}
                  </div>
                </div>
              </div>
            )}

            <div className="mt-3 flex justify-end">
              <a
                href="/app/admin/sharing-leaderboard"
                data-testid="admin-digest-snapshot-open-leaderboard"
                className="text-xs text-[#FF5A1F] hover:text-[#FF5A1F]/80 inline-flex items-center gap-1"
              >
                Open live leaderboard <ArrowSquareOut size={11} />
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Team Recipients Manage Modal */}
      {recipientsModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
          data-testid="admin-digest-snapshot-recipients-modal"
          onClick={(e) => {
            if (e.target === e.currentTarget) setRecipientsModalOpen(false);
          }}
        >
          <div className="card-surface w-full max-w-lg my-6 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
              <div>
                <div className="text-white font-semibold text-sm inline-flex items-center gap-2">
                  <UsersThree size={14} weight="duotone" />
                  Email-to-team recipients
                </div>
                <div className="text-[11px] text-neutral-500 mt-0.5">
                  {recipientsSource === "db" && `${defaultRecipients.length} on the list (managed here).`}
                  {recipientsSource === "env" && `${defaultRecipients.length} from env — add one here to switch to DB.`}
                  {recipientsSource === "empty" && `No recipients yet.`}
                </div>
              </div>
              <button
                onClick={() => setRecipientsModalOpen(false)}
                data-testid="admin-digest-snapshot-recipients-close"
                className="text-neutral-400 hover:text-white p-1 rounded transition-colors"
                aria-label="Close"
              >
                <X size={16} weight="bold" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Add form */}
              <div className="flex gap-2">
                <input
                  type="email"
                  value={newRecipientEmail}
                  onChange={(e) => setNewRecipientEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") addRecipient(); }}
                  placeholder="ops@yourfleet.com"
                  data-testid="admin-digest-snapshot-recipients-new-input"
                  className="flex-1 bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-neutral-500 focus:outline-none focus:border-[#FF5A1F]/40"
                />
                <button
                  onClick={addRecipient}
                  disabled={recipientBusy || !newRecipientEmail.trim()}
                  data-testid="admin-digest-snapshot-recipients-add-btn"
                  className="rounded-lg px-4 py-2 text-xs bg-[#FF5A1F] hover:bg-[#FF5A1F]/90 disabled:opacity-50 text-white font-semibold inline-flex items-center gap-1.5"
                >
                  <Plus size={12} weight="bold" />
                  Add
                </button>
              </div>

              {/* Bulk paste-CSV import */}
              <details
                className="rounded-lg border border-white/10 bg-white/[0.02]"
                data-testid="admin-digest-snapshot-recipients-bulk-details"
              >
                <summary className="cursor-pointer px-3 py-2 text-[11px] text-neutral-300 hover:text-white select-none">
                  Bulk import (paste emails separated by comma, newline, or space)
                </summary>
                <div className="px-3 pb-3 space-y-2">
                  {/* Textarea + overlay `<pre>` — the pre stays behind
                      the textarea with transparent text but underlined
                      spans on invalid emails. Both share identical
                      font-family/size/padding/line-height so the
                      underlines land exactly under the same characters
                      in the interactive textarea. Scroll is synced via
                      onScroll so long pastes keep alignment. */}
                  <div
                    className="relative rounded-lg bg-white/[0.04] border border-white/10 focus-within:border-[#FF5A1F]/40 transition-colors"
                    data-testid="admin-digest-snapshot-recipients-bulk-container"
                  >
                    <pre
                      ref={bulkImportOverlayRef}
                      aria-hidden="true"
                      data-testid="admin-digest-snapshot-recipients-bulk-highlight"
                      className="absolute inset-0 whitespace-pre-wrap break-words px-3 py-2 text-xs font-mono overflow-hidden text-transparent pointer-events-none m-0 leading-normal"
                    >
                      {bulkImportHighlight.map((seg) =>
                        seg.invalid ? (
                          <span
                            key={seg.key}
                            data-testid="admin-digest-snapshot-recipients-bulk-invalid-token"
                            className="underline decoration-2 decoration-wavy decoration-rose-400/80 underline-offset-4"
                          >
                            {seg.text}
                          </span>
                        ) : (
                          <span key={seg.key}>{seg.text}</span>
                        )
                      )}
                      {/* Trailing space so ending newlines still render */}
                      {"\u00A0"}
                    </pre>
                    <textarea
                      ref={bulkImportTextareaRef}
                      value={bulkImportText}
                      onChange={(e) => setBulkImportText(e.target.value)}
                      onScroll={_syncBulkImportScroll}
                      placeholder={"ops@fleet.com\nleads@fleet.com,alex@fleet.com"}
                      rows={4}
                      data-testid="admin-digest-snapshot-recipients-bulk-textarea"
                      className="relative w-full bg-transparent border-0 outline-none focus:outline-none px-3 py-2 text-xs text-white placeholder:text-neutral-600 font-mono resize-y leading-normal"
                    />
                  </div>
                  {bulkImportPreview.total > 0 && (
                    <div
                      className="flex flex-wrap gap-2 text-[10px] uppercase tracking-widest"
                      data-testid="admin-digest-snapshot-recipients-bulk-preview"
                    >
                      <span className="rounded-full px-2 py-0.5 bg-emerald-500/10 text-emerald-300 border border-emerald-500/40">
                        {bulkImportPreview.valid} valid
                      </span>
                      {bulkImportPreview.alreadyOnList > 0 && (
                        <span className="rounded-full px-2 py-0.5 bg-white/[0.04] text-neutral-400 border border-white/10">
                          {bulkImportPreview.alreadyOnList} already on list
                        </span>
                      )}
                      {bulkImportPreview.duplicate > 0 && (
                        <span className="rounded-full px-2 py-0.5 bg-sky-500/10 text-sky-300 border border-sky-500/40">
                          {bulkImportPreview.duplicate} duplicate
                        </span>
                      )}
                      {bulkImportPreview.invalid > 0 && (
                        <span className="rounded-full px-2 py-0.5 bg-rose-500/10 text-rose-300 border border-rose-500/40">
                          {bulkImportPreview.invalid} invalid
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex justify-end">
                    <button
                      onClick={bulkImportRecipients}
                      disabled={bulkImporting || !bulkImportText.trim() || bulkImportPreview.valid === 0}
                      data-testid="admin-digest-snapshot-recipients-bulk-import-btn"
                      className="rounded-full px-4 py-1.5 text-[11px] bg-[#FF5A1F] hover:bg-[#FF5A1F]/90 disabled:opacity-50 text-white font-semibold inline-flex items-center gap-1.5"
                    >
                      <Plus size={11} weight="bold" />
                      {bulkImporting ? "Importing…" : (
                        bulkImportPreview.valid > 0
                          ? `Import ${bulkImportPreview.valid}`
                          : "Import all"
                      )}
                    </button>
                  </div>
                </div>
              </details>

              {/* List */}
              {defaultRecipients.length === 0 && (
                <div
                  className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 text-center text-neutral-500 text-sm"
                  data-testid="admin-digest-snapshot-recipients-empty"
                >
                  Add ops emails above so the Email-to-Team button pre-fills them next time.
                </div>
              )}
              {defaultRecipients.length > 0 && (
                <div
                  className="rounded-lg border border-white/[0.06] bg-white/[0.02] overflow-hidden"
                  data-testid="admin-digest-snapshot-recipients-list"
                >
                  {recipientsSource === "env" && (
                    <div className="px-3 py-2 text-[11px] text-amber-300 border-b border-white/[0.06] bg-amber-500/[0.06]">
                      Reading from env. Add one here to switch to editable DB storage.
                    </div>
                  )}
                  {defaultRecipients.map((email) => {
                    const row = recipientsRows.find((r) => r.email === email);
                    return (
                      <div
                        key={email}
                        data-testid={`admin-digest-snapshot-recipients-row-${email}`}
                        className="flex items-center justify-between px-3 py-2 border-b border-white/[0.04] last:border-b-0"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-white text-sm font-mono truncate">{email}</div>
                          {row?.added_by && (
                            <div className="text-[10px] text-neutral-500 mt-0.5">
                              added by {row.added_by}
                            </div>
                          )}
                        </div>
                        {recipientsSource === "db" && (
                          <button
                            onClick={() => removeRecipient(email)}
                            disabled={recipientBusy}
                            data-testid={`admin-digest-snapshot-recipients-remove-${email}`}
                            className="text-neutral-400 hover:text-rose-400 p-1.5 rounded transition-colors"
                            title={`Remove ${email}`}
                            aria-label={`Remove ${email}`}
                          >
                            <Trash size={12} weight="bold" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
